import { isPlatformServer } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { WebformService, KeyValue } from '@app/services/webform.service';
import { applyXssValidation } from '../validators/xss';
import { Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'webform-actions',
  templateUrl: './actions.component.html',
  styleUrls: ['./actions.component.scss'],
})
export class ActionsComponent implements OnInit, OnDestroy {
  @Input() actions!: any;
  @Input() group!: FormGroup<any>;
  @Output() onSubmit = new EventEmitter<void>();

  hideSubmit: boolean = false;
  hidePrev: boolean = false;
  hideNext: boolean = false;
  currentPage: number = 0;
  steps: KeyValue[] = [];
  #currentPageSub!: Subscription;
  #pagesSub!: Subscription;

  readonly #platformId = inject<string>(PLATFORM_ID);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #webform = inject(WebformService);

  ngOnInit(): void {
    this.hideSubmit = this.actions['#submit_hide'];
    this.hidePrev = this.actions['#wizard_prev_hide'];
    this.hideNext = this.actions['#wizard_next_hide'];
    this.#currentPageSub = this.#webform.currentPage.subscribe((v: number) => {
      this.currentPage = v;
      this.#cdr.markForCheck();
    });
    this.#pagesSub = this.#webform.pages.subscribe((v: KeyValue[]) => {
      this.steps = v;
      this.#cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    if (this.#currentPageSub) this.#currentPageSub.unsubscribe();
    if (this.#pagesSub) this.#pagesSub.unsubscribe();
  }

  submit(): void {
    // Emit event inside angular zone to allow parent with
    // change detection strategy onPush to detect
    this.onSubmit.emit();
  }

  goTo(index: number): void {
    if (isPlatformServer(this.#platformId)) return;
    if (index > this.currentPage) {
      document?.dispatchEvent(new CustomEvent('webform:track:submit:incomplete'));

      const hasXssAttempt = applyXssValidation(this.group);
      if (hasXssAttempt) {
        this.group.markAllAsTouched();
        document
          .querySelector('form')
          ?.querySelectorAll(
            '.ng-invalid:not(.webform-field--processed_text):not(.webform-field--webform_markup):not(.webform-page)'
          )[0]
          ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        document?.dispatchEvent(new CustomEvent('webform:track:form_display'));
        return;
      }
    }

    if (this.group.valid || this.currentPage >= index) {
      this.#webform.setCurrentPage(index);
      document
        ?.querySelector('webform-steps')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      this.group.markAllAsTouched();
      document
        .querySelector('form')
        ?.querySelectorAll(
          '.ng-invalid:not(.webform-field--processed_text):not(.webform-field--webform_markup):not(.webform-page)'
        )[0]
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      document?.dispatchEvent(new CustomEvent('webform:track:form_display'));
    }
  }

  get nextLabel(): string | null {
    if (this.steps[this.currentPage]['#next_button_label'])
      return this.steps[this.currentPage]['#next_button_label'];
    if (this.actions['#wizard_next__label']) return this.actions['#wizard_next__label'];
    return null;
  }

  get prevLabel(): string | null {
    if (this.steps[this.currentPage]['#prev_button_label'])
      return this.steps[this.currentPage]['#prev_button_label'];
    if (this.actions['#wizard_prev__label']) return this.actions['#wizard_prev__label'];
    return null;
  }
}
